import { useState } from 'react';
import './LogTouchpointModal.css';

const TYPES = [
  { key: 'WHATSAPP',          label: 'WhatsApp',        icon: '💬' },
  { key: 'MAIL',              label: 'Mail',             icon: '📧' },
  { key: 'AUDIO_CALL',        label: 'Audio Call',       icon: '📞' },
  { key: 'VIDEO_CALL',        label: 'Video Call',       icon: '📹' },
  { key: 'ONLINE_MEETING',    label: 'Online Meeting',   icon: '💻' },
  { key: 'IN_PERSON_MEETING', label: 'In Person',        icon: '🤝' },
];

const OUTCOMES = {
  WHATSAPP: [
    { key: 'SENT',    label: 'Sent',     positive: false },
    { key: 'REPLIED', label: 'Replied',  positive: true  },
    { key: 'NO_REPLY',label: 'No Reply', positive: false },
  ],
  MAIL: [
    { key: 'SENT',           label: 'Sent',            positive: false },
    { key: 'REPLIED',        label: 'Replied',         positive: true  },
    { key: 'NO_REPLY',       label: 'No Reply',        positive: false },
    { key: 'BOUNCED',        label: 'Bounced',         positive: false },
  ],
  AUDIO_CALL: [
    { key: 'CONNECTED',          label: 'Connected',         positive: true  },
    { key: 'NOT_REACHABLE',      label: 'Not Reachable',     positive: false },
    { key: 'CALLBACK_REQUESTED', label: 'Callback Requested',positive: false },
    { key: 'VOICEMAIL_LEFT',     label: 'Voicemail Left',    positive: false },
  ],
  VIDEO_CALL: [
    { key: 'ATTENDED',    label: 'Attended',    positive: true  },
    { key: 'NO_SHOW',     label: 'No Show',     positive: false },
    { key: 'RESCHEDULED', label: 'Rescheduled', positive: false },
  ],
  ONLINE_MEETING: [
    { key: 'ATTENDED',    label: 'Attended',    positive: true  },
    { key: 'NO_SHOW',     label: 'No Show',     positive: false },
    { key: 'RESCHEDULED', label: 'Rescheduled', positive: false },
  ],
  IN_PERSON_MEETING: [
    { key: 'ATTENDED', label: 'Attended', positive: true  },
    { key: 'NO_SHOW',  label: 'No Show',  positive: false },
  ],
};

const FOLLOW_UP_QUICK = [
  { label: 'Tomorrow', days: 1 },
  { label: 'In 3 days', days: 3 },
  { label: 'Next week', days: 7 },
];

export default function LogTouchpointModal({ lead, onClose, onSaved }) {
  const [type, setType] = useState('');
  const [outcome, setOutcome] = useState('');
  const [notes, setNotes] = useState('');
  const [showConversation, setShowConversation] = useState(false);
  const [rawText, setRawText] = useState('');
  const [duration, setDuration] = useState('');
  const [followUpDate, setFollowUpDate] = useState('');
  const [aumInput, setAumInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  function handleTypeSelect(t) {
    setType(t);
    setOutcome('');
  }

  function handleQuickFollowUp(days) {
    const d = new Date();
    d.setDate(d.getDate() + days);
    setFollowUpDate(d.toISOString().slice(0, 10));
  }

  async function handleSave() {
    if (!type) { setError('Select a touchpoint type'); return; }
    if (!outcome) { setError('Select an outcome'); return; }
    setSaving(true);
    setError('');
    try {
      const body = { type, outcome, loggedByUserId: 'current-user' };
      if (notes.trim()) body.notes = notes.trim();
      if (rawText.trim()) body.rawConversationText = rawText.trim();
      if (duration) body.durationMinutes = parseInt(duration, 10);
      if (followUpDate) body.nextFollowUpAt = new Date(followUpDate).toISOString();
      if (aumInput) body.estimatedAum = parseFloat(aumInput) * 100000;
      await onSaved(body);
      onClose();
    } catch (ex) {
      setError(ex.message);
      setSaving(false);
    }
  }

  const outcomes = type ? OUTCOMES[type] : [];
  const showDuration = ['AUDIO_CALL', 'VIDEO_CALL', 'ONLINE_MEETING', 'IN_PERSON_MEETING'].includes(type);
  const conversationHint = type === 'WHATSAPP'
    ? 'Paste WhatsApp chat export (e.g. [15/03/25, 10:23] Name: message...)'
    : 'Paste or type conversation notes (verbatim)';

  return (
    <div className="ltm__overlay" onClick={onClose}>
      <div className="ltm__modal" onClick={e => e.stopPropagation()}>
        <div className="ltm__header">
          <div>
            <div className="ltm__title">Log Touchpoint</div>
            <div className="ltm__subtitle">{lead.name}</div>
          </div>
          <button className="ltm__close" onClick={onClose}>×</button>
        </div>

        <div className="ltm__body">
          {error && <div className="ltm__error">{error}</div>}

          {/* Step 1: Type */}
          <div className="ltm__section">
            <div className="ltm__section-label">Type</div>
            <div className="ltm__type-grid">
              {TYPES.map(t => (
                <button
                  key={t.key}
                  className={`ltm__type-btn ${type === t.key ? 'ltm__type-btn--selected' : ''}`}
                  onClick={() => handleTypeSelect(t.key)}
                >
                  <span className="ltm__type-icon">{t.icon}</span>
                  <span className="ltm__type-label">{t.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Step 2: Outcome */}
          {type && (
            <div className="ltm__section">
              <div className="ltm__section-label">Outcome <span className="ltm__req">*</span></div>
              <div className="ltm__outcome-row">
                {outcomes.map(o => (
                  <button
                    key={o.key}
                    className={`ltm__outcome-pill ${outcome === o.key ? 'ltm__outcome-pill--selected' : ''} ${o.positive ? 'ltm__outcome-pill--positive' : ''}`}
                    onClick={() => setOutcome(o.key)}
                  >
                    {o.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Notes */}
          {type && (
            <div className="ltm__section">
              <div className="ltm__section-label">Quick note</div>
              <textarea
                className="ltm__notes"
                placeholder="Quick note..."
                rows={2}
                value={notes}
                onChange={e => setNotes(e.target.value)}
              />
            </div>
          )}

          {/* Conversation (expandable) */}
          {type && (
            <div className="ltm__section">
              <button
                className="ltm__expand-btn"
                onClick={() => setShowConversation(prev => !prev)}
              >
                {showConversation ? '▾' : '▸'} Add conversation details
              </button>
              {showConversation && (
                <div className="ltm__conversation">
                  <div className="ltm__section-hint">{conversationHint}</div>
                  <textarea
                    className="ltm__conv-text"
                    placeholder="Paste conversation here..."
                    rows={8}
                    value={rawText}
                    onChange={e => setRawText(e.target.value)}
                  />
                  <div className="ltm__section-hint ltm__section-hint--ai">
                    AI will extract insights when you click 'Process' later
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Duration */}
          {showDuration && type && (
            <div className="ltm__section">
              <div className="ltm__section-label">Duration (minutes, optional)</div>
              <input
                className="ltm__duration"
                type="number"
                min="1"
                placeholder="e.g. 15"
                value={duration}
                onChange={e => setDuration(e.target.value)}
              />
            </div>
          )}

          {/* Follow-up */}
          {type && (
            <div className="ltm__section">
              <div className="ltm__section-label">Next follow-up</div>
              <div className="ltm__followup-row">
                {FOLLOW_UP_QUICK.map(q => (
                  <button
                    key={q.label}
                    className={`ltm__quick-pill ${followUpDate && new Date(followUpDate).toDateString() === (() => { const d = new Date(); d.setDate(d.getDate() + q.days); return d.toDateString(); })() ? 'ltm__quick-pill--selected' : ''}`}
                    onClick={() => handleQuickFollowUp(q.days)}
                  >
                    {q.label}
                  </button>
                ))}
                <input
                  className="ltm__date-input"
                  type="date"
                  value={followUpDate}
                  onChange={e => setFollowUpDate(e.target.value)}
                />
              </div>
            </div>
          )}
          {/* AUM update */}
          {type && (
            <div className="ltm__section">
              <div className="ltm__section-label">Update AUM estimate <span className="ltm__optional">(optional — in Lakhs)</span></div>
              <div className="ltm__aum-row">
                <span className="ltm__aum-prefix">₹</span>
                <input
                  className="ltm__aum-input"
                  type="number"
                  min="0"
                  step="0.5"
                  placeholder={lead.estimated_aum ? (lead.estimated_aum / 100000).toFixed(1) : 'e.g. 50'}
                  value={aumInput}
                  onChange={e => setAumInput(e.target.value)}
                />
                <span className="ltm__aum-suffix">L</span>
              </div>
              {lead.estimated_aum > 0 && !aumInput && (
                <div className="ltm__aum-current">Current: ₹{(lead.estimated_aum / 100000).toFixed(1)}L</div>
              )}
            </div>
          )}
        </div>

        <div className="ltm__footer">
          <button className="btn--primary ltm__save-btn" onClick={handleSave} disabled={saving || !type}>
            {saving ? 'Saving...' : 'Log Touchpoint'}
          </button>
        </div>
      </div>
    </div>
  );
}
